import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, errorText, formatDate, formatQty } from '../api';
import { useAuth } from '../auth';
import { Card, CheckField, Empty, Loading, Notice } from '../components/ui';
import type { StockRow, Warehouse } from '../types';

export function StockPage() {
  const { ownerId } = useAuth();
  const [rows, setRows] = useState<StockRow[] | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [search, setSearch] = useState('');
  const [groupByArticle, setGroupByArticle] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
  }, []);

  const load = useCallback(async () => {
    setError('');
    try {
      setRows(await api<StockRow[]>('/api/stock', { query: { ownerId, warehouseId, search } }));
    } catch (err) {
      setError(errorText(err));
    }
  }, [ownerId, warehouseId, search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  // Verdichtete Sicht: eine Zeile je Artikel und Eigentümer statt je Lagerplatz.
  const grouped = useMemo(() => {
    if (!rows) return [];
    const map = new Map<string, { row: StockRow; qty: number; places: number }>();
    for (const row of rows) {
      const key = `${row.article_id}-${row.owner_id}`;
      const entry = map.get(key);
      if (entry) {
        entry.qty += row.qty;
        entry.places += 1;
      } else {
        map.set(key, { row, qty: row.qty, places: 1 });
      }
    }
    return [...map.values()].sort((a, b) => a.row.article_name.localeCompare(b.row.article_name));
  }, [rows]);

  const total = rows?.reduce((sum, row) => sum + row.qty, 0) ?? 0;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <div className="toolbar">
        <label className="field" style={{ minWidth: 260 }}>
          <span>Suche</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Artikel oder Lagerplatz"
          />
        </label>
        <label className="field">
          <span>Lager</span>
          <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">alle Lager</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.code} · {warehouse.name}
              </option>
            ))}
          </select>
        </label>
        <CheckField label="Nach Artikel verdichten" checked={groupByArticle} onChange={setGroupByArticle} />
        <span className="spacer" />
        <div className="small muted">
          {rows ? `${rows.length} Zeilen · ${formatQty(total)} Einheiten` : ''}
        </div>
      </div>

      <Card padded={false}>
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>Kein Bestand gefunden.</Empty>
        ) : groupByArticle ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th className="num">Lagerplätze</th>
                  <th className="num">Bestand</th>
                </tr>
              </thead>
              <tbody>
                {grouped.map((entry) => (
                  <tr key={`${entry.row.article_id}-${entry.row.owner_id}`}>
                    <td>
                      <span className="mono">{entry.row.sku}</span> {entry.row.article_name}
                    </td>
                    <td className="small">{entry.row.owner_name}</td>
                    <td className="num">{entry.places}</td>
                    <td className="num">{formatQty(entry.qty, entry.row.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Lager</th>
                  <th>Platz</th>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th>Charge</th>
                  <th>MHD</th>
                  <th className="num">Menge</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.location_id}-${row.article_id}-${row.lot_id ?? 'none'}`}>
                    <td className="mono small">{row.warehouse_code}</td>
                    <td className="mono">{row.location_code}</td>
                    <td>
                      <span className="mono">{row.sku}</span> {row.article_name}
                    </td>
                    <td className="small">{row.owner_name}</td>
                    <td>{row.lot_code ?? '—'}</td>
                    <td className="small">{formatDate(row.best_before)}</td>
                    <td className="num">{formatQty(row.qty, row.unit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
